import React from 'react';
import '../../styles/components/AsperaPageHeader.scss';
import { FlexGrid, Row, Column, Button } from '../Carbon';
import { AsperaBreadcrumbs } from '../AsperaBreadcrumbs';
import { AsperaTabs } from '../AsperaTabs';
import { language } from '../../services/LanguageService';
import { AsperaSlideOverPanel } from '../AsperaSlideOverPanel';
import { AsperaError } from '../../classes/AsperaError';
import { AsperaOverflowButton } from '../AsperaOverflowButton';
import { getEnvironmentVar, clearFocus } from '../../helpers/helpers';
import { AsperaTooltip } from '../AsperaTooltip';
/**
 * React Component
 *
 * The AsperaPageHeader renders the page header that contains the breadcrumbs, title and page buttons.
 * Items are optional (other than pageTitle) and render based on what is passed.
 *
 * @example
 * ```javascript
 * <AsperaPageHeader pageTitle="Packages List" breadcrumbs={this.breadcrumbs} />
 * ```
 */
export const AsperaPageHeader = class AsperaPageHeader extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            currentIconTab: undefined,
        };
        /** Avoid re-opening icon tabs if already open */
        this.alteredPageIconTab = false;
        this.closeSlideOver = () => {
            this.alteredPageIconTab = true;
            this.setState({ currentIconTab: undefined });
        };
    }
    getActions() {
        const { actions, overflowAction } = this.props;
        if (Array.isArray(actions) || overflowAction) {
            return (React.createElement("div", { className: "actions" },
                (actions || []).filter(action => !action.hidden).reverse().map((button, index) => {
                    return React.createElement(Button, { kind: button.kind, key: index, onClick: button.onClick, disabled: button.disabled, renderIcon: button.icon, "data-auto": `page-header-${button.name}` }, button.name);
                }),
                !!overflowAction && React.createElement(AsperaOverflowButton, Object.assign({}, overflowAction, { flipped: !language.leftRightStyle }))));
        }
    }
    get iconTabs() {
        const { iconTabs } = this.props;
        return (iconTabs || []).filter(tab => !tab.hidden);
    }
    get showBreadcrumbs() {
        const { breadcrumbs, alwaysShowBreadcrumbs } = this.props;
        return !!((breadcrumbs && breadcrumbs.length > 1) || alwaysShowBreadcrumbs || this.iconTabs.length);
    }
    get iconTabItems() {
        const { currentIconTab } = this.state;
        if (!this.iconTabs.length)
            return null;
        return (React.createElement("div", { className: "aspera-page-header--icon-tabs" }, this.iconTabs.map((tab, index) => {
            return React.createElement(Button, { key: index, hasIconOnly: true, tooltipAlignment: "end", tooltipPosition: "bottom", iconDescription: tab.name, "data-auto": `page-header-icon-${tab.id}`, renderIcon: tab.icon, kind: "ghost", className: `aspera-page-header--icon-tabs-button ${tab.id === (currentIconTab === null || currentIconTab === void 0 ? void 0 : currentIconTab.id) ? 'active' : ''}`, onClick: () => {
                    clearFocus();
                    this.alteredPageIconTab = true;
                    if ((currentIconTab === null || currentIconTab === void 0 ? void 0 : currentIconTab.id) === tab.id) {
                        this.setState({ currentIconTab: undefined });
                    }
                    else {
                        this.setState({ currentIconTab: tab });
                    }
                } });
        })));
    }
    get pageSubTitleTooltip() {
        const { pageSubTitle, subTitleTooltipProps } = this.props;
        return (React.createElement("div", { className: "page-sub-title" },
            React.createElement("span", null, pageSubTitle),
            !!subTitleTooltipProps && React.createElement(AsperaTooltip, Object.assign({ className: "page-sub-title-tooltip" }, subTitleTooltipProps))));
    }
    setDocumentTitle(title) {
        try {
            document.title = title;
        }
        catch (error) {
            new AsperaError('AsperaPageHeader: unable to set page title', error);
        }
    }
    setInitialOpenIconTab() {
        const loadedIconTab = this.iconTabs.filter(tab => tab.initiallyOpen)[0];
        if (loadedIconTab) {
            this.setState({ currentIconTab: loadedIconTab });
        }
    }
    componentDidMount() {
        const { pageTitle } = this.props;
        this.setInitialOpenIconTab();
        if (pageTitle) {
            this.setDocumentTitle(`${pageTitle} - ${getEnvironmentVar('app_name') || ''}`);
        }
    }
    componentWillUnmount() {
        const { pageTitle } = this.props;
        if (pageTitle) {
            this.setDocumentTitle(getEnvironmentVar('app_name') || '');
        }
    }
    componentDidUpdate(previousProps) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const { currentIconTab } = this.state;
        const { pageTitle, pageSubTitle, alwaysShowBreadcrumbs, className, breadcrumbs, actions, tabs, overflowAction, iconTabs } = this.props;
        if (currentIconTab && !this.iconTabs.filter(tab => tab.id === currentIconTab.id)[0]) {
            this.setState({ currentIconTab: undefined });
        }
        if (pageTitle !== previousProps.pageTitle ||
            pageSubTitle !== previousProps.pageSubTitle ||
            alwaysShowBreadcrumbs !== previousProps.alwaysShowBreadcrumbs ||
            className !== previousProps.className ||
            (breadcrumbs === null || breadcrumbs === void 0 ? void 0 : breadcrumbs.length) !== ((_a = previousProps.breadcrumbs) === null || _a === void 0 ? void 0 : _a.length) ||
            (actions === null || actions === void 0 ? void 0 : actions.length) !== ((_b = previousProps.actions) === null || _b === void 0 ? void 0 : _b.length) ||
            (overflowAction === null || overflowAction === void 0 ? void 0 : overflowAction.name) !== ((_c = previousProps.overflowAction) === null || _c === void 0 ? void 0 : _c.name) ||
            (overflowAction === null || overflowAction === void 0 ? void 0 : overflowAction.id) !== ((_d = previousProps.overflowAction) === null || _d === void 0 ? void 0 : _d.id) ||
            ((_e = overflowAction === null || overflowAction === void 0 ? void 0 : overflowAction.items) === null || _e === void 0 ? void 0 : _e.length) !== ((_g = (_f = previousProps.overflowAction) === null || _f === void 0 ? void 0 : _f.items) === null || _g === void 0 ? void 0 : _g.length) ||
            (tabs === null || tabs === void 0 ? void 0 : tabs.length) !== ((_h = previousProps.tabs) === null || _h === void 0 ? void 0 : _h.length) ||
            (iconTabs === null || iconTabs === void 0 ? void 0 : iconTabs.length) !== ((_j = previousProps.iconTabs) === null || _j === void 0 ? void 0 : _j.length)) {
            if (pageTitle && pageTitle !== previousProps.pageTitle) {
                this.setDocumentTitle(`${pageTitle} - ${getEnvironmentVar('app_name') || ''}`);
            }
            if (!this.alteredPageIconTab) {
                this.setInitialOpenIconTab();
            }
            this.setState({});
        }
    }
    render() {
        const { className, pageTitle, tabs, breadcrumbs, viewNotReady, pageImage, pageSubTitle, customActionView } = this.props;
        const { currentIconTab } = this.state;
        return (React.createElement("div", { className: `aspera-page-header ${className ? className : ''}` },
            this.showBreadcrumbs && (React.createElement("div", { className: "aspera-page-header--breadcrumbs-wrapper" },
                React.createElement(FlexGrid, null,
                    React.createElement(Row, null,
                        React.createElement(Column, null,
                            React.createElement("div", { className: "aspera-page-header--breadcrumbs" },
                                React.createElement(AsperaBreadcrumbs, { items: breadcrumbs }),
                                this.iconTabItems)))))),
            React.createElement(FlexGrid, null,
                React.createElement(Row, null,
                    React.createElement(Column, { className: "heading-wrapper" },
                        React.createElement("div", { className: "heading-title" },
                            React.createElement("div", { className: "page-title" },
                                !!pageImage && React.createElement("div", { className: "page-title-image", style: { backgroundImage: `url(${pageImage})` } }),
                                React.createElement("h1", { className: "page-title-text", role: "heading" }, pageTitle)),
                            !!pageSubTitle && this.pageSubTitleTooltip),
                        !!customActionView && customActionView,
                        this.getActions()))),
            !viewNotReady && React.createElement(AsperaTabs, { items: tabs }),
            !!currentIconTab && React.createElement(AsperaSlideOverPanel, { belowBreadcrumbs: true, slideInMode: true, size: currentIconTab.componentSize, title: currentIconTab.name, onClose: this.closeSlideOver, open: true }, currentIconTab.component)));
    }
};
